import { createTheme, type Theme } from '@mui/material/styles'

export type BiThemeId = 'fiesta' | 'exponential' | 'financial-light' | 'financial-dark'

export const THEME_STORAGE_KEY = 'fiesta-bi-theme-id'

export const THEME_OPTIONS: Array<{ id: BiThemeId; label: string }> = [
  { id: 'fiesta', label: 'Fiesta' },
  { id: 'exponential', label: 'Exponential' },
  { id: 'financial-light', label: 'Financial Light' },
  { id: 'financial-dark', label: 'Financial Dark' },
]

/** Cores de gráfico por tema (Recharts não lê MUI automaticamente). */
export type ChartTokens = {
  line: string
  fill: string
  pie: string[]
  logoGradient: string
}

declare module '@mui/material/styles' {
  interface Theme {
    chart: ChartTokens
  }
  interface ThemeOptions {
    chart?: ChartTokens
  }
}

const sharedTypography = {
  fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
  h4: {
    fontFamily: '"Outfit", "IBM Plex Sans", sans-serif',
    fontWeight: 700,
    letterSpacing: '-0.03em',
  },
  h6: {
    fontFamily: '"Outfit", "IBM Plex Sans", sans-serif',
    fontWeight: 600,
    letterSpacing: '-0.02em',
  },
  subtitle2: {
    fontFamily: '"Outfit", "IBM Plex Sans", sans-serif',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    fontSize: '0.68rem',
  },
  button: {
    fontFamily: '"Outfit", "IBM Plex Sans", sans-serif',
    fontWeight: 600,
  },
}

/** Tema Fiesta BI — command center (teal). Padrão. */
export const fiestaTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0f766e',
      light: '#14b8a6',
      dark: '#115e59',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0e7490',
      light: '#22d3ee',
      dark: '#155e75',
    },
    background: {
      default: '#eef3f2',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
    },
    success: { main: '#15803d' },
    warning: { main: '#c2410c' },
    error: { main: '#b91c1c' },
    divider: 'rgba(15, 23, 42, 0.08)',
  },
  typography: sharedTypography,
  shape: { borderRadius: 8 },
  chart: {
    line: '#0f766e',
    fill: '#14b8a6',
    pie: ['#0f766e', '#0e7490', '#0369a1', '#c2410c', '#64748b', '#7c3aed'],
    logoGradient: 'linear-gradient(145deg, #14b8a6 0%, #0f766e 55%, #0e7490 100%)',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(15, 118, 110, 0.4) transparent',
        },
        body: {
          fontFeatureSettings: '"ss01" on, "cv11" on',
          background:
            'radial-gradient(1100px 480px at 8% -8%, rgba(20, 184, 166, 0.2), transparent 58%),' +
            'radial-gradient(900px 420px at 96% 4%, rgba(14, 116, 144, 0.14), transparent 52%),' +
            'linear-gradient(180deg, #f4f8f7 0%, #eef3f2 40%, #e8efed 100%)',
          backgroundAttachment: 'fixed',
        },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(15, 118, 110, 0.35)',
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(15, 118, 110, 0.55)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.94)',
          color: '#0f172a',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.04)',
          border: '1px solid rgba(15, 118, 110, 0.1)',
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 14px rgba(15, 118, 110, 0.28)' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6 },
        sizeSmall: { height: 24 },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: '"Outfit", "IBM Plex Sans", sans-serif',
          fontWeight: 700,
          borderRadius: 12,
        },
      },
    },
  },
})

/**
 * Tema Exponential — eco visual do Exponential Club
 * (https://ricamello.com.br/exponentialclub/).
 *
 * Dark de marca (#120E18), primária #8E5ACE, CTA amarelo #F1E333.
 * Fundo, AppBar e scrollbar seguem o tema (não o Fiesta claro).
 */
export const exponentialTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8E5ACE',
      light: '#A878DB',
      dark: '#6B3FA0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#F1E333',
      light: '#F7EB6A',
      dark: '#C4B100',
      contrastText: '#120E18',
    },
    background: {
      default: '#120E18',
      paper: '#1A1424',
    },
    text: {
      primary: '#F5F0FA',
      secondary: '#B8A9C9',
    },
    success: { main: '#4ade80' },
    warning: { main: '#F1E333' },
    error: { main: '#f87171' },
    divider: 'rgba(168, 120, 219, 0.18)',
    action: {
      hover: 'rgba(142, 90, 206, 0.12)',
      selected: 'rgba(142, 90, 206, 0.22)',
    },
  },
  typography: sharedTypography,
  shape: { borderRadius: 8 },
  chart: {
    line: '#A878DB',
    fill: '#8E5ACE',
    pie: ['#8E5ACE', '#A878DB', '#F1E333', '#f87171', '#94a3b8', '#22d3ee'],
    logoGradient: 'linear-gradient(145deg, #A878DB 0%, #8E5ACE 55%, #6B3FA0 100%)',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(142, 90, 206, 0.55) #120E18',
        },
        body: {
          fontFeatureSettings: '"ss01" on, "cv11" on',
          background:
            'radial-gradient(1000px 520px at 12% -10%, rgba(142, 90, 206, 0.28), transparent 55%),' +
            'radial-gradient(800px 400px at 92% 0%, rgba(241, 227, 51, 0.08), transparent 48%),' +
            'linear-gradient(180deg, #1A1125 0%, #120E18 45%, #0E0A14 100%)',
          backgroundAttachment: 'fixed',
          color: '#F5F0FA',
        },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-track': { background: '#120E18' },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(142, 90, 206, 0.5)',
          borderRadius: 8,
          border: '2px solid #120E18',
          backgroundClip: 'padding-box',
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(168, 120, 219, 0.75)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(18, 14, 24, 0.88)',
          color: '#F5F0FA',
          borderBottom: '1px solid rgba(168, 120, 219, 0.2)',
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(0,0,0,0.35), 0 8px 24px rgba(142, 90, 206, 0.1)',
          border: '1px solid rgba(168, 120, 219, 0.2)',
          backgroundImage: 'none',
          backgroundColor: '#1A1424',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 18px rgba(142, 90, 206, 0.45)' },
        },
        containedSecondary: {
          color: '#120E18',
          fontWeight: 700,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6 },
        sizeSmall: { height: 24 },
        outlined: {
          borderColor: 'rgba(168, 120, 219, 0.35)',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: '"Outfit", "IBM Plex Sans", sans-serif',
          fontWeight: 700,
          borderRadius: 12,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardWarning: {
          backgroundColor: 'rgba(241, 227, 51, 0.12)',
          color: '#F5F0FA',
        },
        standardError: {
          backgroundColor: 'rgba(248, 113, 113, 0.12)',
          color: '#F5F0FA',
        },
      },
    },
  },
})

function createFinancialTheme(mode: 'light' | 'dark'): Theme {
  const dark = mode === 'dark'
  return createTheme({
    palette: dark
      ? {
          mode: 'dark',
          primary: {
            main: '#42a5f5',
            light: '#64b5f6',
            dark: '#1976d2',
            contrastText: '#0d1117',
          },
          secondary: {
            main: '#f48fb1',
            light: '#fce4ec',
            dark: '#c2185b',
            contrastText: '#1a1a1a',
          },
          success: { main: '#66bb6a', light: '#81c784', dark: '#388e3c' },
          warning: { main: '#ffb74d', light: '#ffcc80', dark: '#f57c00' },
          error: { main: '#ef5350', light: '#e57373', dark: '#c62828' },
          info: { main: '#4fc3f7', light: '#81d4fa', dark: '#0288d1' },
          background: { default: '#121212', paper: '#1e1e1e' },
          text: {
            primary: 'rgba(255, 255, 255, 0.87)',
            secondary: 'rgba(255, 255, 255, 0.6)',
            disabled: 'rgba(255, 255, 255, 0.38)',
          },
          divider: 'rgba(255, 255, 255, 0.12)',
          action: {
            active: 'rgba(255, 255, 255, 0.56)',
            hover: 'rgba(255, 255, 255, 0.08)',
            selected: 'rgba(255, 255, 255, 0.16)',
            disabled: 'rgba(255, 255, 255, 0.3)',
            disabledBackground: 'rgba(255, 255, 255, 0.12)',
            focus: 'rgba(255, 255, 255, 0.12)',
          },
        }
      : {
          mode: 'light',
          primary: {
            main: '#1976d2',
            light: '#42a5f5',
            dark: '#1565c0',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#dc004e',
            light: '#ff5983',
            dark: '#9a0036',
            contrastText: '#ffffff',
          },
          success: { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20' },
          warning: { main: '#ed6c02', light: '#ff9800', dark: '#e65100' },
          error: { main: '#d32f2f', light: '#ef5350', dark: '#c62828' },
          info: { main: '#0288d1', light: '#03a9f4', dark: '#01579b' },
          background: { default: '#f5f5f5', paper: '#ffffff' },
        },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2.5rem', fontWeight: 600 },
      h2: { fontSize: '2rem', fontWeight: 600 },
      h3: { fontSize: '1.75rem', fontWeight: 600 },
      h4: { fontSize: '1.5rem', fontWeight: 600 },
      h5: { fontSize: '1.25rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      body1: { fontSize: '1rem', lineHeight: 1.5 },
      body2: { fontSize: '0.875rem', lineHeight: 1.43 },
    },
    shape: { borderRadius: 8 },
    chart: {
      line: dark ? '#64b5f6' : '#1976d2',
      fill: dark ? '#42a5f5' : '#42a5f5',
      pie: dark
        ? ['#42a5f5', '#66bb6a', '#ffb74d', '#f48fb1', '#4fc3f7', '#ab47bc']
        : ['#1976d2', '#2e7d32', '#ed6c02', '#dc004e', '#0288d1', '#7b1fa2'],
      logoGradient: dark
        ? 'linear-gradient(145deg, #64b5f6 0%, #42a5f5 55%, #1976d2 100%)'
        : 'linear-gradient(145deg, #42a5f5 0%, #1976d2 55%, #1565c0 100%)',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: dark ? '#121212' : '#f5f5f5',
            color: dark ? 'rgba(255, 255, 255, 0.87)' : undefined,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: dark ? '#1e1e1e' : '#ffffff',
            color: dark ? 'rgba(255, 255, 255, 0.87)' : '#212121',
            borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: dark
              ? '0px 2px 12px rgba(0,0,0,0.45)'
              : '0px 2px 8px rgba(0,0,0,0.1)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 8, backgroundImage: 'none' },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 8, padding: '8px 16px' },
          contained: { boxShadow: 'none' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
    },
  })
}

export const financialLightTheme = createFinancialTheme('light')
export const financialDarkTheme = createFinancialTheme('dark')

/** @deprecated Preferir fiestaTheme — mantido para imports existentes. */
export const biTheme = fiestaTheme

export const themesById: Record<BiThemeId, Theme> = {
  fiesta: fiestaTheme,
  exponential: exponentialTheme,
  'financial-light': financialLightTheme,
  'financial-dark': financialDarkTheme,
}

export function resolveThemeId(raw: string | null | undefined): BiThemeId {
  if (raw === 'exponential') return 'exponential'
  if (raw === 'financial-light') return 'financial-light'
  if (raw === 'financial-dark') return 'financial-dark'
  return 'fiesta'
}

export function loadStoredThemeId(): BiThemeId {
  try {
    const requested = new URLSearchParams(window.location.search).get('theme')
    if (requested) return resolveThemeId(requested)
    return resolveThemeId(localStorage.getItem(THEME_STORAGE_KEY))
  } catch {
    return 'fiesta'
  }
}

export function storeThemeId(id: BiThemeId): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, id)
  } catch {
    /* ignore */
  }
}
